import { DateUtil, PluralizeUtil } from '@axiom/ui';
import type { TopTask } from '@axiom/validation';
import { TopTaskTypes } from '@axiom/validation';
import moment from 'moment';

import type { WorkSpaceTopTaskCategoryFilter } from '../schema/workspace-top-task-category-filter-schema';

import { TimeCopyUtil } from './time-copy-util';

export const TopTasksUtil = {
  getFilteredTopTasks({
    topTasks,
    filters,
    snoozedTasks,
  }: {
    topTasks: TopTask[];
    filters: Partial<WorkSpaceTopTaskCategoryFilter>;
    snoozedTasks: WorkSpaceTopTaskCategoryFilter['snoozedTasks'];
  }) {
    const selectedSnoozeOptionTopTasks = topTasks.filter(task => {
      const isSnoozedActive =
        task.snoozedAt != null && DateUtil.isSnoozedToday(task.snoozedAt);

      return snoozedTasks ? isSnoozedActive : !isSnoozedActive;
    });

    const filterSet = new Set();
    for (const key in filters) {
      if (filters[key as keyof WorkSpaceTopTaskCategoryFilter]) {
        filterSet.add(key);
      }
    }
    const filteredTopTasks = selectedSnoozeOptionTopTasks.filter(task =>
      filterSet.has(task.topTaskCategory.name)
    );
    const snoozeFilterCount = snoozedTasks ? 1 : 0;
    return {
      filteredTasks:
        filterSet.size === 0 ? selectedSnoozeOptionTopTasks : filteredTopTasks,
      topTaskFilterCount: filterSet.size + snoozeFilterCount,
    };
  },
  getTopTaskBadgeColor: (date: string) => {
    return moment() > moment(date) ? 'dataVizRed' : 'dataVizYellow';
  },

  getTopTaskLink: (task: TopTask) => {
    if (
      [
        TopTaskTypes.TIME_TO_SUBMISSION,
        TopTaskTypes.COOL_CANDIDATES_ON_CLOSED_OPPORTUNITY,
      ].includes(task.topTaskType as string)
    ) {
      return `/opportunity-detail/${task.opportunityId}/talent`;
    }
    if (task.topTaskType === TopTaskTypes.SEND_KICKOFF) {
      return `/opportunity-detail/${task.opportunityId}/onboarding`;
    }
    const talentDetailUrl = `/talent-detail/${task.candidateId}`;
    if (
      task.topTaskType ===
      TopTaskTypes.CANDIDATE_ISSUE_MISSING_PRACTICE_START_YEAR
    ) {
      return `${talentDetailUrl}?editProfile=true`;
    }
    return task.topTaskType ===
      TopTaskTypes.EXPERIENCE_DESCRIPTION_LENGTH_EXCEPTION
      ? `${talentDetailUrl}?openExperiences=true`
      : talentDetailUrl;
  },

  getTopTaskCopy: (task: TopTask) => {
    let daysOpen: number;
    let daysCopy: string;

    switch (task.topTaskType) {
      case TopTaskTypes.CANDIDATE_ISSUE_MISSING_ADDRESS_FIELDS: {
        return {
          header: 'Talent Address Incomplete',
          description: `Pricing is blocked for ${task.candidateCalculatedDisplayName}`,
        };
      }

      case TopTaskTypes.CANDIDATE_ISSUE_MISSING_FOCUS_AREA: {
        return {
          header: 'Talent Focus Area Needed',
          description: `Pricing is blocked for ${task.candidateCalculatedDisplayName}`,
        };
      }

      case TopTaskTypes.CANDIDATE_ISSUE_MISSING_BUSINESS_TITLE: {
        return {
          header: 'Talent Legal Role Needed',
          description: `Pricing is blocked for ${task.candidateCalculatedDisplayName}`,
        };
      }

      case TopTaskTypes.CANDIDATE_ISSUE_MISSING_PRACTICE_START_YEAR: {
        return {
          header: 'Years Practicing is Needed',
          description: `Pricing is blocked for ${task.candidateCalculatedDisplayName}`,
        };
      }

      case TopTaskTypes.EXPERIENCE_DESCRIPTION_LENGTH_EXCEPTION: {
        return {
          header: 'Review experience description',
          description: `${task.candidateCalculatedDisplayName} has requested an exception for an experience description.`,
        };
      }

      case TopTaskTypes.COOL_CANDIDATES_ON_CLOSED_OPPORTUNITY: {
        return {
          header: `Cool Talent - Auto Reject ${moment(task.taskExpectedCompletionDateTime).calendar()}`,
          description: `Talent need to be cooled, ${task.opportunityJobName} is now closed.`,
        };
      }

      case TopTaskTypes.CHECK_IN: {
        return {
          header: `${TimeCopyUtil.getDaysCopy(
            Math.floor(
              moment
                .duration(moment().diff(moment(task.taskExpectedStartDateTime)))
                .as('days')
            )
          ).toLocaleLowerCase()} since last check in`,
          description: task.candidateCalculatedDisplayName,
        };
      }

      case TopTaskTypes.IDLE_REMINDER: {
        daysOpen = Math.floor(
          moment
            .duration(
              moment().diff(
                moment(task.taskExpectedCompletionDateTime).add(8, 'days')
              )
            )
            .as('days')
        );
        daysCopy = TimeCopyUtil.getDaysCopy(Math.abs(daysOpen)).toLowerCase();

        return {
          header: `${daysCopy} ${daysOpen < 0 ? 'until ' : ''}idle`,
          description: task.candidateCalculatedDisplayName,
        };
      }

      case TopTaskTypes.CANDIDATE_REPUTATION_FEEDBACK: {
        daysOpen = DateUtil.getCountFromDateToDate(
          task.taskExpectedStartDateTime,
          new Date()
        );

        return {
          header: `Talent feedback - ${daysOpen > 0 ? `${daysOpen} ${PluralizeUtil({ text: 'day', count: daysOpen })} ago` : 'Today'}`,
          description: `${task.candidateCalculatedDisplayName} - ${task.opportunityJobName}`,
        };
      }

      case TopTaskTypes.CANDIDATE_REPUTATION_COACHING_ALERT: {
        daysOpen = DateUtil.getCountFromDateToDate(
          task.taskExpectedStartDateTime,
          new Date()
        );

        return {
          header: `Coaching - Talent feedback - ${daysOpen > 0 ? `${daysOpen} ${PluralizeUtil({ text: 'day', count: daysOpen })} ago` : 'Today'}`,
          description: `${task.candidateCalculatedDisplayName} - ${task.opportunityJobName}`,
        };
      }

      case TopTaskTypes.CANDIDATE_REPUTATION_EXPECTATIONS_NOT_MET: {
        daysOpen = DateUtil.getCountFromDateToDate(
          task.taskExpectedStartDateTime,
          new Date()
        );

        return {
          header: `Low Score - Talent feedback - ${daysOpen > 0 ? `${daysOpen} ${PluralizeUtil({ text: 'day', count: daysOpen })} ago` : 'Today'}`,
          description: `${task.candidateCalculatedDisplayName} - ${task.opportunityJobName}`,
        };
      }

      case TopTaskTypes.SEND_KICKOFF: {
        return {
          header: 'Review/Send onboarding details',
          description: `Publish Onboarding for ${task.opportunityJobName}`,
        };
      }

      default: {
        return {
          header: `Submission needed - Open ${TimeCopyUtil.getHoursOrDaysCopyFromNumberOfHours(
            TimeCopyUtil.getHoursDifferenceExcludingWeekends(
              task.taskExpectedStartDateTime,
              moment().format()
            )
          ).toLowerCase()}`,
          description: task.opportunityJobName,
        };
      }
    }
  },

  isCandidateIssue: (task: TopTask) => {
    return (
      task.topTaskType ===
        TopTaskTypes.CANDIDATE_ISSUE_MISSING_ADDRESS_FIELDS ||
      task.topTaskType === TopTaskTypes.CANDIDATE_ISSUE_MISSING_FOCUS_AREA ||
      task.topTaskType === TopTaskTypes.CANDIDATE_ISSUE_MISSING_BUSINESS_TITLE
    );
  },

  isReputationIssue: (task: TopTask) => {
    return (
      task.topTaskType === TopTaskTypes.CANDIDATE_REPUTATION_FEEDBACK ||
      task.topTaskType === TopTaskTypes.CANDIDATE_REPUTATION_COACHING_ALERT ||
      task.topTaskType ===
        TopTaskTypes.CANDIDATE_REPUTATION_EXPECTATIONS_NOT_MET
    );
  },
};
